import { enableProdMode, NgZone } from '@angular/core';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';
import * as Sentry from '@sentry/angular-ivy';
import { captureConsoleIntegration } from '@sentry/integrations';

import { AppModule } from './app/app.module';
import { runSentryProver } from './app/core/sentry-prover';
import { environment } from './environments/environment';
import { buildInfo } from './environments/build-info';
import '@angular/compiler';
/* ngx-image-cropper (preview-avatar) uses pinch-to-zoom via HammerJS if
   present, else logs a console warning - forwarded to Sentry as
   SC-SAAS-FRONTEND-9/SAN-123. HammerJS was never installed; this makes
   pinch-zoom actually work instead of just silencing the warning. */
import 'hammerjs';

/* Key names scrubbed (case-insensitive substring match) from event payloads
   before send - verify_tenant's response (cached client-side) carries
   azure_client_secret + SMTP credentials in plaintext, so any request body
   or header captured by Sentry must never include them unredacted. */
const SENTRY_SCRUB_KEYS = ['password', 'secret', 'token', 'api_key', 'apikey', 'authorization', 'client_secret'];

function isSentryScrubbedKey(key: string): boolean {
  const lower = key.toLowerCase();
  return SENTRY_SCRUB_KEYS.some((k) => lower.includes(k));
}

function scrubSentryValue(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(scrubSentryValue);
  }
  if (value && typeof value === 'object') {
    const result: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(value as Record<string, unknown>)) {
      result[key] = isSentryScrubbedKey(key) ? '[Filtered]' : scrubSentryValue(val);
    }
    return result;
  }
  return value;
}

/* Must run before the prod console-suppression block below reassigns
   console.log/error to no-ops - if Sentry initialized after that, any of
   its own init-time warnings (bad DSN, etc.) would be silently swallowed
   in production and a broken setup would look identical to a working one. */
Sentry.init({
  dsn: environment.sentryDsn,
  environment: environment.environmentName,
  release: `sc-saas-frontend@${buildInfo.commitHash}`,
  integrations: [
    Sentry.browserTracingIntegration(),
    // Forwards console.warn calls as Sentry events - the closest
    // approximation to the other repos' Logs pillar available on
    // angular-ivy@7 (the version this Angular 13 app is pinned to; the
    // real structured Logs API - Sentry.logger.* - only exists in the SDK's
    // v8+ mainline, which angular-ivy was never upgraded to).
    // Deliberately 'warn' only, NOT 'error': the production block below
    // reassigns console.error to a no-op whenever environment.production
    // is true (which includes environment.staging.ts too), running AFTER
    // Sentry's console instrumentation patches it - so an 'error' level
    // here would silently stop capturing in exactly the builds where it
    // matters most. console.warn isn't touched by that block, so it works
    // everywhere. Real unhandled exceptions are still fully covered via
    // the ErrorHandler above, independent of this integration.
    captureConsoleIntegration({ levels: ['warn'] }),
  ],
  tracesSampleRate: environment.production ? 0.1 : 1.0,
  sendDefaultPii: false,
  beforeSend(event, hint) {
    // "[webpack-dev-server] WARNING/Warnings while compiling." - forwarded via
    // captureConsoleIntegration's console.warn hook during `ng serve` builds.
    // webpack-dev-server only exists in a local dev server process; it is
    // never present in a deployed build's bundle, so this prefix can only
    // ever originate from someone's local machine, regardless of what
    // `environment` tag the event carries. SAN-168 through SAN-184 (17
    // tickets, one per source file compiled that session) were each
    // separately triaged as dev noise; drop the whole class at ingestion
    // instead of re-triaging it every time someone runs `ng serve`.
    if (typeof event.message === 'string' && event.message.startsWith('[webpack-dev-server]')) {
      return null;
    }

    // SAN-1573 -- a developer's own `ng serve` session (localhost / 127.0.0.1) is not a user
    // and has the browser console; its events only crowded the issue list. The deployed dev
    // servers (thub.sanchidev.in and the others) still report.
    if (location.hostname === 'localhost' || location.hostname === '127.0.0.1') {
      return null;
    }

    // SAN-1573 -- NG0100 (ExpressionChangedAfterItHasBeenChecked) comes from Angular's
    // dev-mode double check, which never runs once enableProdMode() is called, so it can only
    // come from a non-production build (SAN-158, SC-SAAS-FRONTEND-9M, B2, BV). Production
    // builds are untouched: there the condition is false.
    if (!environment.production && /\bNG0100\b/.test(event.exception?.values?.[0]?.value || '')) {
      return null;
    }

    // SAN-1573 / SC-SAAS-FRONTEND-11 -- ngx-ui-loader console.warn()s "loaderId ... does not
    // exist." when start()/stop() runs while no matching <ngx-ui-loader> is on screen; nothing
    // breaks (the spinner just isn't shown). The thrown `loaderId "master" is duplicated` crash is
    // an Error, not this warning, so it is still reported (SAN-1623).
    if (
      !event.exception &&
      event.level === 'warning' &&
      typeof event.message === 'string' &&
      /^\[ngx-ui-loader\] - loaderId "[^"]*" does not exist\./.test(event.message)
    ) {
      return null;
    }

    // Code -32603 is the standard Ethereum JSON-RPC "Internal error" - a
    // browser crypto-wallet extension (e.g. MetaMask) injecting
    // window.ethereum and rejecting a promise, unrelated to app code
    // (SC-SAAS-FRONTEND-90 / SAN-376: zero first-party stack frames).
    const exceptionValue = event.exception?.values?.[0]?.value || '';
    if (exceptionValue.includes('-32603') && exceptionValue.includes('JSON-RPC')) {
      return null;
    }
    // SAN-1900 - when the wallet rejects with a plain object
    // ({ code: -32603, message: 'Internal JSON-RPC error.' }) the exception
    // value is the SDK's synthetic "Object captured as promise rejection with
    // keys: code, message", so the check above never matches; the code and
    // message only exist on hint.originalException.
    const walletRejection = hint?.originalException as any;
    if (
      walletRejection &&
      typeof walletRejection === 'object' &&
      !(walletRejection instanceof Error) &&
      walletRejection.code === -32603 &&
      typeof walletRejection.message === 'string' &&
      walletRejection.message.includes('JSON-RPC')
    ) {
      return null;
    }

    // "_AutofillCallbackHandler" is a global injected by Instagram/Facebook's
    // in-app-browser (WebView) autofill script on iOS, not app code - the
    // stack trace's only "frame" is the page's own <!DOCTYPE...> markup, i.e.
    // zero first-party frames (SC-SAAS-FRONTEND-9X / SAN-503).
    if (exceptionValue.includes('_AutofillCallbackHandler')) {
      return null;
    }

    // Same class: an iOS in-app browser (WKWebView) host app evaluating its
    // own injected callback - `(null)('cs_<UUID>')` - after the page's global
    // it expects isn't there. Frame is the page's <!DOCTYPE...> markup at 1:7,
    // zero first-party frames; the app never defines any `cs_` callback
    // (SC-SAAS-FRONTEND-GC).
    if (/^\(null\) is not a function\. \(In '\(null\)\('cs_[0-9A-F-]{36}'\)'/i.test(exceptionValue)) {
      return null;
    }

    // SC-SAAS-FRONTEND-3G — every event is headless Chrome 126 from Microsoft
    // Azure IPs (a mail link-scanner sandbox opening emailed links such as
    // /account/edit/email/notification/<email>). Its injected `<anonymous>`
    // script calls zone's `__zone_symbol__addEventListener` directly, bypassing
    // zone's event-name registration, so zone's own dispatch reads
    // zoneSymbolEventNames[type]['false'] of undefined. Not a real user/app path.
    const firstFrames = event.exception?.values?.[0]?.stacktrace?.frames || [];
    if (
      exceptionValue === "Cannot read properties of undefined (reading 'false')" &&
      firstFrames.some((f) => (f.function || '').includes('__zone_symbol__addEventListener'))
    ) {
      return null;
    }

    // SAN-1143 / SC-SAAS-FRONTEND-GA, -D1 — `SyntaxError` thrown while parsing
    // `beacon.min.js`, a third-party analytics script injected into the page
    // (the `beacon.min.js/v<hash>` form matches Cloudflare's web-analytics
    // beacon, though that's inferred) and versioned externally. Not part of
    // this repo's bundle, so nothing here can fix it; every frame is that
    // script. Narrow on purpose: SyntaxError AND a beacon.min.js frame.
    const beaconFrames = event.exception?.values?.[0]?.stacktrace?.frames || [];
    if (
      event.exception?.values?.[0]?.type === 'SyntaxError' &&
      beaconFrames.some((f) => /beacon\.min\.js/.test(f.filename || f.module || ''))
    ) {
      return null;
    }

    // SAN-1668 / SC-SAAS-FRONTEND-A2..A6, 9H, 4H, 70, 8Y, 8Z, 9Q, 9R, 9W, CT, CV, CW, GD,
    // E1, 9V, 41, 3Q — errors raised by code that is not in this bundle: browser extensions
    // (a "Gen AI Tracker" / "Rpc Adaptor" injection, an autofill extension's
    // `execute_auto_fill`, `runtime.sendMessage`, the locator.js dev tool, Cently), crypto-wallet
    // extensions (MetaMask / polkadot) and the Android in-app-browser bridge
    // (`iabjs://navigation_performance_logger_android`). None can be fixed from here and each was
    // seen on 1-18 users' browsers. Matched on the message / exception text only, each pattern
    // anchored to the foreign component's own wording, so a first-party error that merely
    // mentions a wallet or an extension is still reported.
    const noiseValue = event.exception?.values?.[0]?.value;
    const noiseType = event.exception?.values?.[0]?.type;
    // Sentry stores an exception as type + value (the "Type: value" in an issue title is
    // composed), and a console.error/warn event carries only event.message; test each form.
    const noiseCandidates = [
      event.message,
      noiseValue,
      noiseType && noiseValue ? `${noiseType}: ${noiseValue}` : undefined,
    ].filter((t): t is string => typeof t === 'string');
    const noiseFrames = (event.exception?.values?.[0]?.stacktrace?.frames || []).map(
      (f) => f.filename || f.module || ''
    );
    const FOREIGN_NOISE: RegExp[] = [
      /\[Injection\]\[[A-Z]\] \[(?:Rpc Adaptor|Gen AI Tracker)\]/,
      /^Error restoring session \w*:? ?(?:Failed to connect to MetaMask|Transport request timed out)/,
      /^ObjectMultiplex - orphaned data for stream "metamask-/,
      /^@polkadot\/util(?:-crypto)? (?:requires direct dependencies|has multiple versions)/,
      /^(?:ReferenceError: )?xbrowser is not defined/,
      /^(?:Error: )?Invalid call to runtime\.sendMessage\(\)\. Tab not found\./,
      /^\[locator-?js\]/i,
      /^\[cently:rd:site\]/,
      // SAN-1982 / SC-SAAS-FRONTEND-HK: HiBrowser (Infinix/Transsion stock browser) console.warn()s
      // its own page-load timing JSON on every navigation; no such log exists in this bundle.
      /^\{"dnsLookup":\d+,"tcpConnect":\d+,.*"totalLoadTime":\d+.*\}$/,
    ];
    if (
      noiseCandidates.some((text) => FOREIGN_NOISE.some((re) => re.test(text))) ||
      (noiseCandidates.some((text) => /^(?:Error: )?Error invoking /.test(text)) &&
        noiseFrames.some((f) => /^iabjs:\/\//.test(f)))
    ) {
      return null;
    }

    // SAN-1144 / SC-SAAS-FRONTEND-4B — "Object captured as promise rejection
    // with keys: code, message, status" carries no stack frame, so the call
    // site that rejects a raw HTTP-error-like object is unidentifiable from the
    // event alone. The rejected object itself is in hint.originalException:
    // attach its code/status/message/url as extra data + a tag so the NEXT
    // occurrence names the failing request. Diagnostic only - the event is
    // still sent, nothing is dropped.
    const rejected = hint?.originalException as any;
    if (
      rejected &&
      typeof rejected === 'object' &&
      !(rejected instanceof Error) &&
      ('status' in rejected || 'code' in rejected)
    ) {
      event.tags = { ...event.tags, rejection_status: String(rejected.status ?? rejected.code) };
      event.extra = {
        ...event.extra,
        rejection: {
          code: rejected.code,
          status: rejected.status,
          message: typeof rejected.message === 'string' ? rejected.message.slice(0, 200) : undefined,
          url: typeof rejected.url === 'string' ? rejected.url.split('?')[0] : undefined,
        },
      };
    }

    // The HTTP interceptor (AddTokenHeaderHttpRequestInterceptor) already fully
    // handles 401/403: it clears storage, toasts the user, logs them out and
    // redirects. It then re-throws so callers can still react - which means each
    // of the ~173 service-level console.warn(`Label( <message> )`) sites logs the
    // SAME already-handled fault a second time, and captureConsoleIntegration
    // above turns every one of those into an event. That is expected behaviour
    // being reported as a problem: SAN-471 / SC-SAAS-FRONTEND-5Q, -28, -2A, -4G,
    // -2Y, ~118 events across ~104 users.
    //
    // Dropped here rather than at the 173 call sites - that is the whack-a-mole
    // c0638d20 started (3 sites) and could not finish.
    //
    // Deliberately narrow; all three conditions must hold:
    //   1. no exception - a genuine throw mentioning the same text still reports
    //   2. warn level only - never filter the level wholesale, it is the Logs pillar
    //   3. message ENDS in the `Label( <handled message> )` shape
    //
    // Same class, added for SAN-599/SAN-601/SAN-608/SAN-600: the OTP-verify
    // (AuthService.verifyOtp / SignUpService.verifyOTP) and login-rate-limit
    // (AuthService.login) catchError blocks already console.warn + toast (or
    // leave the caller's own error callback to toast) the same handled fault
    // that gets logged here a second time - same double-report pattern as the
    // 401/403 case above, not an uncaught/over-logging bug.
    const HANDLED_HTTP_FAULT = /\(\s*(Invalid access token|Forbidden resource|Invalid one time password\. Please try again|One time password you entered is expired\.|This verification request has already expired or completed|No such verification request found in the system\. Please try again\.|An account with a similar (?:mobile number|email address) already exists\. Please verify your (?:number|email) or recover your existing account if necessary\.|Oops! It seems like you've reached the maximum allowed rate limit for accessing this action\. Rate limiting is in place to ensure fair usage and maintain the quality of service for all users\.)\s*\)$/;
    if (
      !event.exception &&
      event.level === 'warning' &&
      HANDLED_HTTP_FAULT.test((event.message || '').trim())
    ) {
      return null;
    }
    // SAN-1673 / SC-SAAS-FRONTEND-2J -- the THROWN counterpart of the SAN-471 case above. The
    // interceptor has already handled a 401 completely (cleared storage, toasted "Session expired",
    // logged the user out, redirected to /auth/login; only a background auto-save 401 is silent, by
    // design) and then re-throws; a caller with no error handler lets that HttpErrorResponse reach
    // Angular's ErrorHandler, which reports an expired session as an unhandled error. 401 ONLY: a 403
    // on a SKIP_FORBIDDEN_REDIRECT request is handled by its own caller (not toasted by the
    // interceptor), and 0 / other 4xx / 5xx can mean a missing handler or a real outage, so those are
    // still reported.
    const unhandledHttpValue = event.exception?.values?.[0]?.value;
    if (
      typeof unhandledHttpValue === 'string' &&
      /^Http failure response for \S+: 401 /.test(unhandledHttpValue)
    ) {
      return null;
    }
    // SC-SAAS-FRONTEND-60 -- Popper.js's own dev warning (console.warn, forwarded by
    // captureConsoleIntegration) for a popper element that has a CSS margin. Cosmetic, no user
    // impact; every event came from a local dev session and the margin could not be attributed to
    // one element (SAN-476 / SAN-563 concluded no code change). Narrow on purpose: no exception,
    // warning level, exact message prefix.
    if (
      !event.exception &&
      event.level === 'warning' &&
      typeof event.message === 'string' &&
      event.message.startsWith('Popper: CSS "margin" styles cannot be used to apply padding')
    ) {
      return null;
    }
    // SAN-1982 / SC-SAAS-FRONTEND-HK -- a page-load timing report ({"dnsLookup":..,"totalLoadTime":..,
    // "success":true}) console.warn()ed by a script the browser injects (seen only on HiBrowser,
    // Android). No such code exists in this repo or its node_modules; it is a successful timing,
    // not a fault. Narrow on purpose: no exception, warning level, and the message is that exact
    // JSON shape (starts with dnsLookup, has totalLoadTime).
    if (
      !event.exception &&
      event.level === 'warning' &&
      typeof event.message === 'string' &&
      /^\{"dnsLookup":\d+,"tcpConnect":\d+,.*"totalLoadTime":\d+.*\}$/.test(event.message)
    ) {
      return null;
    }
    if (event.request?.data) {
      event.request.data = scrubSentryValue(event.request.data);
    }
    if (event.request?.headers) {
      event.request.headers = scrubSentryValue(event.request.headers) as Record<string, string>;
    }
    return event;
  },
});

// Logged before the prod console-suppression below reassigns console.log to a
// no-op — this call already ran by then, so the banner survives on production
// builds too. Reload the page and check devtools console to confirm which
// commit/branch a given deploy is actually serving.
console.log(
  '%cSanchiConnect build info',
  'font-weight:bold;color:#fff;background:#2563eb;padding:2px 6px;border-radius:3px;',
  {
    commit: buildInfo.commitHash,
    branch: buildInfo.branch,
    commitDate: buildInfo.commitDate,
    builtAt: buildInfo.buildTime,
    production: environment.production,
    apiBaseUrl: environment.apiEndpoints.baseUrl,
  }
);

if (environment.production) {
  enableProdMode();
  if (window) {
    window['log'] = console.log;
    window['error'] = console.error;
    window['debug'] = console.debug;
    window['info'] = console.debug;
  }

  console.log = console.debug = console.error = console.info = () => { };
}

// SC-SAAS-FRONTEND-G5 — bootstrap.js (angular.json scripts) delegates keydown on
// every `.dropdown-menu` to Dropdown.dataApiKeydownHandler, which assumes a
// sibling [data-bs-toggle="dropdown"] exists. ngx-intl-tel-input's country
// picker (dynamic-forms phone field, e.g. /programs/apply) is an ngx-bootstrap
// dropdown with no such toggle, so ArrowUp/ArrowDown/Escape inside it crashed
// with "reading 'parentNode'". Stop those keydowns at <html> (bubble phase,
// after the element's own handlers, before bootstrap's document listener) only
// for ngx-intl-tel-input's own `.country-dropdown` menu. Scoped that tightly on
// purpose: ng-bootstrap's ngbDropdownMenu also carries `.dropdown-menu` and no
// data-bs-toggle, and its Escape-to-close listens on document keydown - a
// broader match would break Escape on every ngbDropdown. ngx-intl-tel-input
// closes on keyup.esc, so its own behaviour is unaffected.
document.documentElement.addEventListener('keydown', (event: KeyboardEvent) => {
  if (!['ArrowUp', 'ArrowDown', 'Escape'].includes(event.key)) {
    return;
  }
  const menu = (event.target as Element)?.closest?.('ngx-intl-tel-input .dropdown-menu.country-dropdown');
  if (menu && !menu.parentElement?.querySelector('[data-bs-toggle="dropdown"]')) {
    event.stopPropagation();
  }
});




// platformBrowserDynamic().bootstrapModule(AppModule)
//   .catch(err => console.error(err));


// const appSetting = new CometChat.AppSettingsBuilder().setRegion(COMETCHAT_CONSTANTS.REGION).subscribePresenceForAllUsers().build();
// // CometChat.CallSettings()
// CometChat.init(COMETCHAT_CONSTANTS.APP_ID, appSetting).then(() => {

//   console.log('app is ready to work');

platformBrowserDynamic().bootstrapModule(AppModule)
  /* SAN-1572: ?sentry-prove=captured|uncaught, outside production builds only. */
  .then(moduleRef => runSentryProver(environment.environmentName, window.location.search, moduleRef.injector.get(NgZone)))
  .catch(err => console.error(err));
// }, (error) => {
//   console.log('Error In Init', error);
// });


